<!--
  THE SPLASH, AS A SHOWCASE PIECE.

  The About page used to open with an advert-shaped block (`HeroAd`) — a
  trailer in a frame, selling a title rather than the app. The owner: get rid
  of the ad, put the splash screen there, and make the page inspiring.

  So this is the first-run intro's composition, rebuilt to LOOP and to live in
  a box rather than over the whole screen. It is deliberately NOT `Intro.svelte`
  reused: that component is a gate. It writes `markIntroSeen()`, calls `done()`,
  covers the viewport and unmounts itself — mounting it here would mean the real
  intro never played again on this device, which is exactly the bug the
  sessionStorage key exists to prevent. Shared vocabulary, separate machines.

  WHAT IT KEEPS from the ident: the same seeded tile field (so the colours are
  the brand's own and identical on every device), the same easing curves, the
  same "the J becomes the wordmark" gesture. What it changes: it runs on a
  cycle with a hold and a soft reset, it is sized from its OWN box instead of
  the viewport, and it pauses when scrolled out of view or when the tab is
  hidden — an animation nobody is looking at is just battery.

  REDUCED MOTION gets the finished lockup, still. Same rule as everywhere else.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { INTRO_KEY } from './intro';
  import { J, LETTERS, WORD_ASPECT, glyphSrc } from './wordmark';

  const PALETTE = ['#EF2034', '#FF653C', '#F0B64D', '#B4D464', '#32BA8E', '#40BED8'];
  /* The letter table is the shared one (wordmark.ts): this panel and the real
     intro must draw the same word, and a private copy here was the third of
     three that had to be edited by hand when the word changed. The tiles carry
     the intro's cache key for the same reason the intro's do. */
  const asset = (glyph: string): string => glyphSrc(glyph, INTRO_KEY);

  const rand = (seed: string): number => {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return ((h >>> 0) % 100000) / 100000;
  };
  const TILES = Array.from({ length: 14 }, (_, i) => ({
    i,
    hue: PALETTE[Math.floor(rand(`t${i}h`) * PALETTE.length)]!,
    x: (rand(`t${i}x`) - 0.5) * 2.1,
    y: (rand(`t${i}y`) - 0.5) * 1.5,
    delay: rand(`t${i}d`) * 1.8,
    dur: 1.15 + rand(`t${i}u`) * 0.75,
    spin: (rand(`t${i}r`) - 0.5) * 16
  }));

  /* The ident runs 4.9s; this holds the finished lockup for a beat, then
     fades the whole thing and starts again. Long enough to read, short
     enough that a visitor sees the gesture without waiting for it. */
  const DURATION = 4.9;
  const HOLD = 1.9;
  const CYCLE = DURATION + HOLD;

  let t = $state(0);
  let boxW = $state(640);
  let boxH = $state(360);
  let reduced = $state(false);
  let node = $state<HTMLDivElement | null>(null);

  const clamp = (v: number, a = 0, b = 1): number => Math.min(b, Math.max(a, v));
  const outQuint = (p: number): number => 1 - Math.pow(1 - clamp(p), 5);
  const inOut = (p: number): number => {
    const c = clamp(p);
    return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
  };
  const ramp = (from: number, to: number, ease = inOut): number =>
    ease(clamp((t - from) / (to - from)));
  const settle = (delay: number, period = 0.62, damp = 5.2): number => {
    const p = t - delay;
    if (p <= 0) return 0;
    return 1 - Math.exp(-damp * p) * Math.cos((2 * Math.PI * p) / period);
  };

  /* Sized from the element, not the window: this is a panel on a page. */
  const layout = $derived.by(() => {
    const min = Math.min(boxW, boxH);
    return {
      wordH: Math.min(min * 0.26, (boxW * 0.72) / WORD_ASPECT),
      heroH: min * 0.42,
      tileH: min * 0.3,
      spreadX: boxW * 0.34,
      spreadY: boxH * 0.34,
      perspective: min * 1.1
    };
  });

  const become = $derived(ramp(2.95, 3.9));
  const arrive = $derived(settle(2.2));
  const fadeIn = $derived(ramp(2.15, 2.45, outQuint));
  /* The whole panel breathes out at the end of the hold and back in. */
  const cycleFade = $derived(t > DURATION + HOLD - 0.55 ? 1 - ramp(DURATION + HOLD - 0.55, CYCLE) : 1);

  const rig = $derived.by(() => {
    const L = layout;
    const wordW = L.wordH * WORD_ASPECT;
    const jWord = { w: wordW * J.w, h: L.wordH * J.h };
    const jHero = { h: L.heroH, w: L.heroH * (J.w / J.h) * WORD_ASPECT };
    const scale = reduced ? 1 : 0.66 + arrive * 0.34;
    const jW = (jHero.w + (jWord.w - jHero.w) * become) * scale;
    const jH = (jHero.h + (jWord.h - jHero.h) * become) * scale;
    const unitW = jW / J.w;
    const unitH = jH / J.h;
    /* J centred at t=0; the whole word centred at t=1 — `unitW` is the word's
       width, so the second I is already inside it. Same rig as Intro.svelte. */
    const originX = (-jW / 2 - J.x * unitW) * (1 - become) + (-unitW / 2) * become;
    return { unitW, unitH, originX, originY: -unitH / 2 };
  });

  onMount(() => {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      t = DURATION;
      return;
    }

    const el = node;
    if (!el) return;

    const measure = (): void => {
      const r = el.getBoundingClientRect();
      boxW = r.width;
      boxH = r.height;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    /* Runs only while it is on screen AND the tab is visible. */
    let onScreen = false;
    const io = new IntersectionObserver((entries) => {
      onScreen = entries.some((e) => e.isIntersecting);
      if (onScreen) start();
      else stop();
    }, { threshold: 0.15 });
    io.observe(el);

    let raf = 0;
    let last = 0;
    const tick = (now: number): void => {
      if (last === 0) last = now;
      /* Capped: a tab restored after a minute must not jump a whole cycle. */
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      t = (t + dt) % CYCLE;
      raf = window.requestAnimationFrame(tick);
    };
    const start = (): void => {
      if (raf || document.hidden) return;
      last = 0;
      raf = window.requestAnimationFrame(tick);
    };
    const stop = (): void => {
      if (!raf) return;
      window.cancelAnimationFrame(raf);
      raf = 0;
    };
    const visibility = (): void => (document.hidden || !onScreen ? stop() : start());
    document.addEventListener('visibilitychange', visibility);

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', visibility);
    };
  });
</script>

<div class="splash" bind:this={node} aria-hidden="true" style:opacity={cycleFade}>
  {#if !reduced}
    <div class="tunnel" style:perspective="{layout.perspective}px">
      {#each TILES as tile (tile.i)}
        {@const p = clamp((t - tile.delay) / tile.dur)}
        {@const z = -layout.perspective * 2.1 * (1 - outQuint(p))}
        {@const gone = clamp((t - tile.delay - tile.dur) / 0.45)}
        <div
          class="poster"
          style:background={tile.hue}
          style:width="{layout.tileH * 0.68}px"
          style:height="{layout.tileH}px"
          style:opacity={p <= 0 ? 0 : (1 - gone) * 0.92}
          style:transform="translate3d({tile.x * layout.spreadX * outQuint(p)}px, {tile.y *
            layout.spreadY *
            outQuint(p)}px, {z}px) rotate({tile.spin * (1 - outQuint(p))}deg)"
        ></div>
      {/each}
    </div>
  {/if}

  <div class="stage" style:opacity={reduced ? 1 : fadeIn}>
    <div class="lockup">
      {#each LETTERS as letter, i (letter.n)}
        <!-- Stagger index 1..4: O, V, I, then the second I, in reading order. -->
        {@const stagger = i === 0 ? 1 : clamp((become - i * 0.07) / 0.5)}
        <img
          class="glyph"
          src={asset(letter.glyph)}
          alt=""
          style:left="{rig.originX + letter.x * rig.unitW}px"
          style:top="{rig.originY + letter.y * rig.unitH}px"
          style:width="{letter.w * rig.unitW}px"
          style:height="{letter.h * rig.unitH}px"
          style:opacity={reduced ? 1 : stagger}
        />
      {/each}
    </div>
  </div>
</div>

<style>
  .splash {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: radial-gradient(120% 90% at 50% 20%, #2a0a11 0%, #140409 55%, #0a0607 100%);
    transition: opacity 240ms linear;
  }
  .tunnel,
  .stage {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    transform-style: preserve-3d;
  }
  .poster {
    position: absolute;
    border-radius: 10px;
    box-shadow: 0 24px 60px rgb(0 0 0 / 0.5);
    will-change: transform, opacity;
  }
  .lockup {
    position: relative;
    width: 0;
    height: 0;
  }
  /* app.css sets `img { max-width: 100% }`, which collapses a glyph inside a
     zero-width anchor to nothing. Same trap as Logo.svelte. */
  .glyph {
    position: absolute;
    max-width: none;
    object-fit: fill;
    filter: drop-shadow(0 10px 30px rgb(0 0 0 / 0.45));
  }
</style>
